/** Route slug ↔ this module (`/legal/marriage-family-law`). */
export const MARRIAGE_FAMILY_LAW_SLUG = "marriage-family-law";

/**
 * CMS-ready defaults. Icons are lucide-export names where used in UI mappings.
 */
export const marriageFamilyLawDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1505664194779-8beaceb93744?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "3", label: "Marriage types" },
    { value: "21d", label: "Registration" },
    { value: "Family", label: "Legal focus" },
  ],
  funnelTagline:
    "Designed for real users (Ghanaians) + structured for InfoBoxx funnel (education → trust → conversion)",
  focusAreas: [
    "Marriage registration",
    "Divorce processes",
    "Child custody",
    "Prenuptial agreements",
  ],
  introduction: {
    headline: "WHY MARRIAGE & FAMILY LAW MATTERS IN GHANA",
    lead: "Marriage in Ghana is not just personal—it is legal, cultural, and family-based.",
    bullets: [
      "It affects property, children, inheritance, and finances",
      "Mistakes can lead to legal disputes, financial loss, and emotional stress",
      "Many people act based on tradition without understanding the law",
    ],
    whetherYouAreLead: "Whether you are:",
    scenarios: [
      "getting married",
      "planning divorce",
      "dealing with custody",
      "or protecting your assets",
    ],
  },
  marriageTypesSectionTitle: "TYPES OF MARRIAGE IN GHANA (VERY IMPORTANT)",
  marriageTypesIntro: "Ghana recognizes three main types of marriage:",
  marriageTypes: [
    {
      number: 1,
      title: "Customary Marriage",
      bullets: [
        "Based on traditional rites (family + bride price)",
        "Most common in Ghana",
        "Can exist without formal registration",
      ],
    },
    {
      number: 2,
      title: "Civil Marriage (Ordinance Marriage)",
      bullets: [
        "Conducted at Registrar General’s Department",
        "Fully legal and documented",
        "Gives stronger legal protection",
      ],
    },
    {
      number: 3,
      title: "Islamic Marriage",
      bullets: ["Based on Islamic law", "Governed by religious principles"],
    },
  ],
  marriageTypesKeyInsight: {
    label: "Key Insight",
    lead: "Each type of marriage has different legal implications, especially for:",
    bullets: ["divorce", "property", "custody"],
    footer: "Many disputes happen because people don’t understand this difference",
  },
  marriageRegistration: {
    title: "MARRIAGE REGISTRATION PROCESS (STEP-BY-STEP)",
    civilHeading: "For Civil Marriage",
    stepsIntro: "Steps:",
    steps: [
      "File notice at Registrar’s office",
      "Wait for publication period (usually 21 days)",
      "Marriage ceremony conducted",
      "Marriage certificate issued",
    ],
    requiredDocumentsHeading: "Required Documents",
    requiredDocuments: ["ID cards", "Passport photos", "Witnesses", "Consent (if required)"],
    whyHeading: "Why Registration Matters",
    whyLead: "Gives legal recognition",
    whyAlso: "Protects rights of spouses",
    whyRequiredIntro: "Required for:",
    whyRequired: ["property ownership", "immigration", "inheritance"],
    mistakesHeading: "Common Mistakes",
    mistakes: [
      "Not registering customary marriage",
      "Losing marriage certificate",
      "Assuming traditional marriage = full legal protection",
    ],
  },
  divorce: {
    title: "DIVORCE IN GHANA (MOST ASKED QUESTION)",
    keyLawHeading: "Key Law",
    keyLaw: "Divorce is governed by the Matrimonial Causes Act, 1971 (Act 367)",
    importantRuleHeading: "IMPORTANT RULE",
    importantRuleLead: "Divorce is NOT automatic in Ghana",
    importantRuleBody: "You must prove:",
    importantRuleProof: "The marriage has broken down beyond reconciliation",
    groundsHeading: "Common Grounds for Divorce",
    grounds: ["Adultery", "Unreasonable behavior", "Desertion", "Separation"],
    processHeading: "Step-by-Step Divorce Process",
    processSteps: [
      "File a divorce petition in court",
      "Serve notice to spouse",
      "Court hearings",
      "Evidence presented",
      "Court grants divorce",
    ],
    courtsLead: "Divorce cases are handled in:",
    courts: ["District Court", "Circuit Court", "High Court"],
    courtDecidesHeading: "What the Court Decides",
    courtDecides: ["Child custody", "Maintenance (financial support)", "Property division"],
    notesHeading: "Important Notes",
    notes: [
      "You generally cannot file for divorce within the first 2 years of marriage unless special conditions apply",
      "Both parties must go through legal process, not just agreement",
    ],
  },
  childCustody: {
    title: "CHILD CUSTODY IN GHANA",
    sensitivity: "This is one of the most sensitive issues.",
    principleHeading: "Key Principle",
    principleLead: "The court always considers:",
    principleQuote: "“The best interest of the child”",
    factorsHeading: "Factors Considered",
    factors: [
      "Child’s age",
      "Emotional needs",
      "Stability of each parent",
      "Financial ability",
      "Living environment",
    ],
    typesHeading: "Types of Custody",
    types: ["Sole custody (one parent)", "Joint custody (both parents)"],
    visitationHeading: "Visitation Rights",
    visitationLead: "Even if one parent has custody:",
    visitationBullet: "The other parent usually gets access rights",
    issuesHeading: "Common Issues",
    issues: [
      "Parents fighting over children",
      "Denying access to the other parent",
      "Using children as leverage",
    ],
  },
  prenuptial: {
    title: "PRENUPTIAL AGREEMENTS (GROWING IN DEMAND)",
    whatHeading: "What is a Prenup?",
    whatLead:
      "A legal agreement signed before marriage to define:",
    whatBullets: ["asset ownership", "financial responsibilities", "property division"],
    whyHeading: "Why It Matters",
    whyBullets: [
      "Protects personal assets",
      "Prevents disputes during divorce",
      "Clarifies financial expectations",
    ],
    misconceptionHeading: "Common Misconception",
    misconceptionLead: 'People think prenups are:',
    misconceptionQuoted: '"only for rich people"',
    misconceptionTruthLead: "In reality:",
    misconceptionTruth: "They are for anyone who wants clarity and protection",
  },
  propertyDivision: {
    title: "PROPERTY & ASSET DIVISION",
    intro: "When marriage ends:",
    courtHeading: "The Court May Decide",
    courtBullets: [
      "Who gets what property",
      "How assets are shared",
      "Maintenance obligations",
    ],
    insightHeading: "Important Insight",
    insightBody:
      "Property acquired during marriage is often considered:",
    insightEmphasis: "Joint property",
    problemsHeading: "Common Problems",
    problems: [
      "One partner hiding assets",
      "Disputes over land/property",
      "No documentation",
    ],
  },
  commonProblemsGhana: {
    title: "COMMON PROBLEMS GHANAIANS FACE",
    intro: "Top Issues",
    issues: [
      "Unregistered marriages",
      "Difficult divorce processes",
      "Child custody disputes",
      "Property fights after divorce",
      "Family interference",
      "Lack of legal guidance",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS FOR GHANAIANS",
    beforeMarriage: {
      heading: "Before Marriage",
      items: ["Understand the type of marriage", "Consider a prenup", "Register your marriage"],
    },
    duringMarriage: {
      heading: "During Marriage",
      items: ["Keep financial records", "Document assets", "Maintain transparency"],
    },
    duringDivorce: {
      heading: "During Divorce",
      items: ["Stay calm", "Gather documents", "Speak to a lawyer early"],
    },
    forChildren: {
      heading: "For Children",
      items: ["Focus on their welfare", "Avoid conflict in front of them", "Respect court decisions"],
    },
  },
  redFlags: {
    title: "RED FLAGS TO WATCH OUT FOR",
    items: [
      "Partner hiding assets",
      "No marriage documentation",
      "Pressure to sign unclear agreements",
      "Refusal to involve legal professionals",
      "Family controlling decisions",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hook: { label: "Hook", text: '"How do I file for divorce in Ghana?"' },
    education: { label: "Education", text: "Explain process + requirements" },
    authority: { label: "Authority", text: "Legal framework + court role" },
    conversion: { label: "Conversion", text: '"Speak to a verified lawyer"' },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    bullets: [
      "Marriage in Ghana is not just emotional—it is legal and financial",
      "Divorce is not simple—it requires proof and legal process",
      "Child custody is not about parents—it is about the child’s best interest",
    ],
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "The difference between:",
    smooth: "a smooth marriage or separation",
    and: "and",
    stressful: "a stressful legal battle",
    closing: "…is legal awareness and early guidance",
  },
  faqs: [
    {
      q: "What types of marriage does Ghana recognize?",
      a: "Ghana recognizes three main types: Customary Marriage, Civil Marriage (Ordinance Marriage) at the Registrar General’s Department, and Islamic Marriage. Each type has different legal implications, especially for divorce, property, and custody.",
    },
    {
      q: "How do I register a civil marriage in Ghana?",
      a: "Steps: File notice at Registrar’s office, wait for the publication period (usually 21 days), have the marriage ceremony conducted, and receive your marriage certificate. You typically need ID cards, passport photos, witnesses, and consent if required.",
    },
    {
      q: "Is divorce automatic in Ghana?",
      a: "No. Divorce is NOT automatic in Ghana. You must prove the marriage has broken down beyond reconciliation. Divorce is governed by the Matrimonial Causes Act, 1971 (Act 367), and you generally cannot file within the first 2 years of marriage unless special conditions apply.",
    },
    {
      q: "How does the court decide child custody?",
      a: "The court always considers “The best interest of the child” — including the child’s age, emotional needs, stability of each parent, financial ability, and living environment. Custody may be sole or joint, and the other parent usually still gets access rights.",
    },
    {
      q: "Are prenuptial agreements only for rich people?",
      a: "No — that is a common misconception. Prenups are for anyone who wants clarity and protection. They define asset ownership, financial responsibilities, and property division before marriage.",
    },
  ],
  articles: [
    {
      id: "mfl-a-1",
      slug: "marriage-family-law-1",
      title: "How to Register a Marriage in Ghana (Step-by-Step)",
      excerpt:
        "Steps: File notice at Registrar’s office; wait for publication period (usually 21 days); marriage ceremony conducted; marriage certificate issued. Required documents: ID cards; passport photos; witnesses; consent (if required). Registration gives legal recognition and protects spouses; required for property ownership, immigration, inheritance.",
      type: "Guide",
      readTime: "7 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: 'Read this InfoBoxx guide: "How to Register a Marriage in Ghana (Step-by-Step)"',
      coverImage:
        "https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?auto=format&fit=crop&w=800&q=80",
      tags: ["Marriage registration", "Ghana", "Ordinance"],
      pullQuote:
        "You need clear legal understanding before making decisions",
      takeaways: [
        "Understand the type of marriage",
        "Consider a prenup",
        "Register your marriage",
      ],
      video: {
        title: "Mistakes couples make",
        duration: "3:52",
        poster:
          "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "MARRIAGE REGISTRATION PROCESS (STEP-BY-STEP)",
          text: "For Civil Marriage",
        },
        {
          heading: "Steps:",
          text: "",
          bullets: [
            "1. File notice at Registrar’s office",
            "2. Wait for publication period (usually 21 days)",
            "3. Marriage ceremony conducted",
            "4. Marriage certificate issued",
          ],
        },
        {
          heading: "Required Documents",
          text: "",
          bullets: ["ID cards", "Passport photos", "Witnesses", "Consent (if required)"],
        },
        {
          heading: "Why Registration Matters",
          bullets: ["Gives legal recognition", "Protects rights of spouses"],
        },
        {
          heading: "Required for:",
          bullets: ["property ownership", "immigration", "inheritance"],
        },
        {
          heading: "Common Mistakes",
          text: "",
          bullets: [
            "Not registering customary marriage",
            "Losing marriage certificate",
            "Assuming traditional marriage = full legal protection",
          ],
        },
      ],
    },
    {
      id: "mfl-a-2",
      slug: "marriage-family-law-2",
      title: "Divorce in Ghana: What You Must Know Before Filing",
      excerpt:
        "Divorce is governed by the Matrimonial Causes Act, 1971 (Act 367). Divorce is NOT automatic in Ghana — you must prove the marriage has broken down beyond reconciliation. Common grounds include adultery, unreasonable behavior, desertion, separation. Divorce cases are handled in District Court, Circuit Court, High Court. The court decides child custody, maintenance (financial support), property division.",
      type: "Expert Explainer",
      readTime: "9 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: 'Read this InfoBoxx guide: "Divorce in Ghana: What You Must Know Before Filing"',
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      tags: ["Divorce", "Matrimonial Causes", "Courts"],
      pullQuote: "The marriage has broken down beyond reconciliation",
      takeaways: ["Stay calm", "Gather documents", "Speak to a lawyer early"],
      video: {
        title: "Divorce process explained",
        duration: "5:08",
        poster:
          "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Key Law",
          text: "Divorce is governed by the Matrimonial Causes Act, 1971 (Act 367)",
        },
        {
          heading: "IMPORTANT RULE",
          text: "",
          bullets: [
            "Divorce is NOT automatic in Ghana",
            "You must prove:",
            "The marriage has broken down beyond reconciliation",
          ],
        },
        {
          heading: "Common Grounds for Divorce",
          text: "",
          bullets: ["Adultery", "Unreasonable behavior", "Desertion", "Separation"],
        },
        {
          heading: "Step-by-Step Divorce Process",
          text: "",
          bullets: [
            "1. File a divorce petition in court",
            "2. Serve notice to spouse",
            "3. Court hearings",
            "4. Evidence presented",
            "5. Court grants divorce",
          ],
        },
        {
          heading: "Divorce cases are handled in:",
          text: "",
          bullets: ["District Court", "Circuit Court", "High Court"],
        },
        {
          heading: "What the Court Decides",
          text: "",
          bullets: ["Child custody", "Maintenance (financial support)", "Property division"],
        },
        {
          heading: "Important Notes",
          text: "",
          bullets: [
            "You generally cannot file for divorce within the first 2 years of marriage unless special conditions apply",
            "Both parties must go through legal process, not just agreement",
          ],
        },
      ],
    },
    {
      id: "mfl-a-3",
      slug: "marriage-family-law-3",
      title: "Who Gets Custody of a Child in Ghana?",
      excerpt:
        "The court always considers: “The best interest of the child”. Factors considered include child’s age, emotional needs, stability of each parent, financial ability, living environment. Types of custody: sole custody (one parent); joint custody (both parents). Visitation: even if one parent has custody, the other parent usually gets access rights.",
      type: "Checklist",
      readTime: "6 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: 'Read this InfoBoxx guide: "Who Gets Custody of a Child in Ghana?"',
      coverImage:
        "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=800&q=80",
      tags: ["Custody", "Children", "Family law"],
      pullQuote: "The best interest of the child",
      takeaways: [
        "Focus on their welfare",
        "Avoid conflict in front of them",
        "Respect court decisions",
      ],
      video: {
        title: "Child custody tips",
        duration: "4:36",
        poster:
          "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "CHILD CUSTODY IN GHANA",
          text: "This is one of the most sensitive issues.",
        },
        {
          heading: "Key Principle",
          text: "",
          bullets: [
            "The court always considers:",
            "“The best interest of the child”",
          ],
        },
        {
          heading: "Factors Considered",
          text: "",
          bullets: [
            "Child’s age",
            "Emotional needs",
            "Stability of each parent",
            "Financial ability",
            "Living environment",
          ],
        },
        {
          heading: "Types of Custody",
          text: "",
          bullets: ["Sole custody (one parent)", "Joint custody (both parents)"],
        },
        {
          heading: "Visitation Rights",
          text: "",
          bullets: ["Even if one parent has custody:", "The other parent usually gets access rights"],
        },
        {
          heading: "Common Issues",
          text: "",
          bullets: [
            "Parents fighting over children",
            "Denying access to the other parent",
            "Using children as leverage",
          ],
        },
      ],
    },
  ],
  videos: [
    {
      id: "mfl-v-1",
      title: "Divorce process explained",
      duration: "5:08",
      poster:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      description:
        "From petition and service through hearings — what proving breakdown beyond reconciliation looks like in practice.",
    },
    {
      id: "mfl-v-2",
      title: "Child custody tips",
      duration: "4:36",
      poster:
        "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=800&q=80",
      description:
        "Best interest of the child, stability, access rights, and why conflict in front of children works against both parents.",
    },
    {
      id: "mfl-v-3",
      title: "Mistakes couples make",
      duration: "3:52",
      poster:
        "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
      description:
        "Unregistered marriages, lost certificates, undocumented assets, and when to involve a verified family lawyer early.",
    },
  ],
  podcasts: [
    {
      id: "mfl-p-1",
      episodeLabel: "Episode 1",
      duration: "29 min",
      title: "Act 367 in Plain Language: Divorce When It’s Really Over",
      description:
        "Grounds for divorce, the two-year rule, and what judges weigh on maintenance and property alongside custody.",
      host: "Afrimore Legal Desk",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "mfl-p-2",
      episodeLabel: "Episode 2",
      duration: "24 min",
      title: "Customary vs Ordinance Marriage: Registration Pitfalls",
      description:
        "Why traditional rites alone may not give the protection you think — and how registration changes immigration and inheritance outcomes.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "April 2026",
      coverImage:
        "https://images.unsplash.com/photo-1518568814500-bf0f8d125f46?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "mfl-p-3",
      episodeLabel: "Mini",
      duration: "16 min",
      title: "Prenups Without the Stigma",
      description:
        "Clarity on assets before marriage — not only for wealthy couples — aligned with mediation-friendly separation planning.",
      host: "Afrimore Legal Desk",
      publishedAt: "March 2026",
      coverImage:
        "https://images.unsplash.com/photo-1521791055364-18956bb5568e?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
